import React, { createContext, useCallback, useContext, useMemo, useRef } from "react";
import { useUnsavedChangesGuard, type UseUnsavedChangesGuardOptions } from "./useUnsavedChangesGuard";
import { UnsavedChangesModal } from "./UnsavedChangesModal";

export type UnsavedChangesGuardContextValue = {
    requestProceed: (proceed: () => void) => void;
    registerBlockerReset: (reset: (() => void) | null) => void;
};

const UnsavedChangesGuardContext = createContext<UnsavedChangesGuardContextValue | null>(null);

export type UnsavedChangesGuardProviderProps = UseUnsavedChangesGuardOptions & {
    children: React.ReactNode;
};

export function UnsavedChangesGuardProvider({
    children,
    ...guardOptions
}: UnsavedChangesGuardProviderProps) {
    const blockerResetRef = useRef<(() => void) | null>(null);

    const releaseBlockedNavigation = useCallback(() => {
        blockerResetRef.current?.();
        blockerResetRef.current = null;
    }, []);

    const guard = useUnsavedChangesGuard({
        ...guardOptions,
        onBeforeDismiss: releaseBlockedNavigation,
    });

    const registerBlockerReset = useCallback((reset: (() => void) | null) => {
        blockerResetRef.current = reset;
    }, []);

    const contextValue = useMemo(
        (): UnsavedChangesGuardContextValue => ({
            requestProceed: guard.requestProceed,
            registerBlockerReset,
        }),
        [guard.requestProceed, registerBlockerReset]
    );

    return (
        <UnsavedChangesGuardContext.Provider value={contextValue}>
            {children}
            <UnsavedChangesModal {...guard.modalProps} />
        </UnsavedChangesGuardContext.Provider>
    );
}

export function useUnsavedChangesGuardContext(): UnsavedChangesGuardContextValue | null {
    return useContext(UnsavedChangesGuardContext);
}
